import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, Link, SidebarHandle, useSidebar, TextField, Wordmark, navigate, takeNewRequest, useLocation, useToast } from '@dugzi/ui';
import { Search, UploadCloud } from 'lucide-react';
import { api } from '../lib/api';
import type { DriveNode, Me, Usage } from '../lib/types';
import * as uploads from '../lib/uploads';
import { clearG, gPending, markG } from '../lib/keys';
import { AccountMenu } from './AccountMenu';
import { Sidebar } from './Sidebar';
import { Browser, type View } from './Browser';
import { UploadPanel } from './UploadPanel';

const SettingsDialog = lazy(() => import('./SettingsDialog'));
const ImportDialog = lazy(() => import('./ImportDialog'));
const Shortcuts = lazy(() => import('./Shortcuts'));

export function viewFrom(path: string, search: string): View {
  const q = new URLSearchParams(search);
  const m = /^\/folders\/(fld_[a-z0-9]+)/.exec(path);
  if (m) return { kind: 'folder', id: m[1] };
  if (path === '/recent') return { kind: 'recent' };
  if (path === '/starred') return { kind: 'starred' };
  if (path === '/shared') return { kind: 'shared' };
  if (path === '/trash') return { kind: 'trash' };
  if (path === '/search') return { kind: 'search', q: q.get('q') ?? '', type: q.get('type') ?? 'any', modified: q.get('modified') ?? 'any' };
  return { kind: 'folder', id: 'root' };
}

export interface FolderCtx { id: string; canEdit: boolean; name: string }

/** The app: top bar, side bar, the file list, uploads, and the global shortcuts. */
export function Shell({ me, setUsage }: { me: Me; setUsage(u: Usage): void }) {
  const toast = useToast();
  const { path, search } = useLocation();
  const view = viewFrom(path, search);
  const [folder, setFolder] = useState<FolderCtx | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [treeKey, setTreeKey] = useState(0);
  const [newFolder, setNewFolder] = useState(false);
  const [settings, setSettings] = useState(false);
  // Import into Drive (D-161): opened from New, or when the provider sends the person back (?import=<job>).
  const [importing, setImporting] = useState<{ job: string | null; step: string | null; error: string | null } | null>(() => {
    const q = new URLSearchParams(location.search);
    const j = q.get('import');
    return j ? { job: j === 'none' || j === 'signin' ? null : j, step: q.get('step'), error: q.get('error') ?? (j === 'signin' ? 'signin' : null) } : null;
  });
  const [keys, setKeys] = useState(false);
  const [dropping, setDropping] = useState(false);
  const [q, setQ] = useState(view.kind === 'search' ? view.q : '');
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const reloadUsage = useCallback(() => { api<Usage>('/api/me/usage').then(setUsage).catch(() => {}); }, [setUsage]);
  const bump = useCallback(() => { setRefresh((r) => r + 1); reloadUsage(); }, [reloadUsage]);
  // Uploads go to the folder on screen (from the address, so a drop right after opening a folder
  // never lands in the previous one); to My Drive when this folder is view-only.
  const here = view.kind === 'folder' ? (view.id === 'root' ? me.rootId : view.id) : null;
  const target = here ? (folder?.id === here && !folder.canEdit ? 'root' : here) : 'root';

  useEffect(() => uploads.onUploaded((n: DriveNode) => {
    setRefresh((r) => r + 1);
    if (n.parentId && n.parentId !== folder?.id) setTreeKey((k) => k + 1);
    reloadUsage();
  }), [folder?.id, reloadUsage]);

  useEffect(() => { if (view.kind !== 'search') setQ(''); }, [view.kind]);

  // "New …" shortcuts from the launcher in other apps don't apply here; tidy the address if present.
  useEffect(() => { takeNewRequest(); }, []);

  const pickFiles = useCallback(() => fileInput.current?.click(), []);
  const pickFolder = useCallback(() => folderInput.current?.click(), []);

  // Global shortcuts (not while typing; the list handles its own keys).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest('input, textarea, [contenteditable="true"], dialog, .dz-layer, [role="menu"]')) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (gPending() && e.key !== 'g') {
        const to = ({ h: '/', r: '/recent', s: '/starred', w: '/shared', t: '/trash' } as Record<string, string>)[e.key.toLowerCase()];
        clearG();
        if (to) { e.preventDefault(); navigate(to); return; }
      }
      if (e.key === 'g') { markG(); return; }
      if (e.key === '/') { e.preventDefault(); searchRef.current?.focus(); }
      else if (e.key === '?') { e.preventDefault(); setKeys(true); }
      else if (e.key === 'F' && e.shiftKey) { e.preventDefault(); setNewFolder(true); }
      else if (e.key === 'u') { e.preventDefault(); pickFiles(); }
      else if (e.key === 'U' && e.shiftKey) { e.preventDefault(); pickFolder(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pickFiles, pickFolder]);

  // Files dragged in from the computer, anywhere on the page.
  useEffect(() => {
    let depth = 0;
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
    const enter = (e: DragEvent) => { if (!hasFiles(e)) return; depth++; setDropping(true); };
    const leave = (e: DragEvent) => { if (!hasFiles(e)) return; depth = Math.max(0, depth - 1); if (!depth) setDropping(false); };
    const over = (e: DragEvent) => { if (hasFiles(e)) e.preventDefault(); };
    const drop = async (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault(); depth = 0; setDropping(false);
      if ((e.target as HTMLElement).closest('[data-drop-folder]')) return; // a folder row took it
      const files = await uploads.filesFromDrop(e.dataTransfer!);
      if (files.length) {
        uploads.enqueue(files, target);
        if (folder && !folder.canEdit) toast.show('You can only view this folder, so the files go to My Drive.');
      }
    };
    window.addEventListener('dragenter', enter);
    window.addEventListener('dragleave', leave);
    window.addEventListener('dragover', over);
    window.addEventListener('drop', drop);
    return () => { window.removeEventListener('dragenter', enter); window.removeEventListener('dragleave', leave); window.removeEventListener('dragover', over); window.removeEventListener('drop', drop); };
  }, [target, folder, toast]);

  // On a phone the bar is full (brand, bell, apps, account): a short placeholder that fits; the label keeps the full name.
  const [phone, setPhone] = useState(() => window.matchMedia('(max-width: 600px)').matches);
  useEffect(() => { const m = window.matchMedia('(max-width: 600px)'); const on = () => setPhone(m.matches); m.addEventListener('change', on); return () => m.removeEventListener('change', on); }, []);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const p = new URLSearchParams(view.kind === 'search' ? search : '');
    p.set('q', q.trim());
    navigate(`/search?${p}`);
  };

  const sidebar = useSidebar('drive', 'nav', { defaultWidth: 232, min: 180, max: 400 });
  return (
    <div className="app">
      <header className="top">
        <a className="dz-skip" href="#main">Skip to files</a>
        <Link to="/" className="top__brand" aria-label="Dugzi Drive home"><Wordmark height={22} /><span className="top__label">Drive</span></Link>
        <form className="search" role="search" onSubmit={submitSearch}>
          <Search aria-hidden size={16} />
          <label className="sr-only" htmlFor="drive-search">Search in Drive</label>
          <input id="drive-search" ref={searchRef} type="search" value={q} placeholder={phone ? 'Search' : 'Search in Drive'} autoComplete="off"
            onChange={(e) => {
              setQ(e.target.value);
              const p = new URLSearchParams(view.kind === 'search' ? search : '');
              p.set('q', e.target.value);
              navigate(`/search?${p}`, view.kind === 'search');
            }}
            data-testid="search" />
        </form>
        <div className="top__actions"><AccountMenu me={me} onSettings={() => setSettings(true)} onShortcuts={() => setKeys(true)} /></div>
      </header>
      <div className="body" {...sidebar.hostProps}>
        <SidebarHandle sb={sidebar} label="Resize Drive navigation" />
        <Sidebar me={me} view={view} treeKey={treeKey}
          onUpload={pickFiles} onUploadFolder={pickFolder} onNewFolder={() => setNewFolder(true)}
          canCreate={!folder || folder.canEdit} onMoved={bump} onSettings={() => setSettings(true)} onImport={() => setImporting({ job: null, step: null, error: null })} />
        <main id="main" className="main" tabIndex={-1}>
          <Browser me={me} view={view} refresh={refresh} onChanged={() => { bump(); setTreeKey((k) => k + 1); }}
            onFolder={setFolder} onUpload={pickFiles} onNewFolder={() => setNewFolder(true)} />
        </main>
      </div>
      <input ref={fileInput} type="file" multiple hidden onChange={(e) => { if (e.target.files?.length) uploads.enqueue(Array.from(e.target.files).map((file) => ({ file })), target); e.target.value = ''; }} data-testid="file-input" />
      <input ref={folderInput} type="file" multiple hidden {...{ webkitdirectory: '' }} onChange={(e) => { if (e.target.files?.length) uploads.enqueue(uploads.filesFromPicker(e.target.files), target); e.target.value = ''; }} data-testid="folder-input" />
      <UploadPanel />
      {dropping && (
        <div className="dropzone" aria-hidden="true">
          <div className="dropzone__card"><UploadCloud size={40} /><p>Drop to upload to <strong>{folder?.canEdit ? folder.name : 'My Drive'}</strong></p></div>
        </div>
      )}
      {newFolder && <NewFolderDialog parentId={target} onClose={(made) => { setNewFolder(false); if (made) { bump(); setTreeKey((k) => k + 1); } }} />}
      <Suspense fallback={null}>
        {settings && <SettingsDialog me={me} onClose={() => { setSettings(false); reloadUsage(); bump(); }} />}
        {keys && <Shortcuts onClose={() => setKeys(false)} />}
        {importing && <ImportDialog jobId={importing.job} step={importing.step} error={importing.error}
          onClose={() => { setImporting(null); if (location.search.includes('import=')) navigate('/', true); reloadUsage(); bump(); setTreeKey((k) => k + 1); }}
          onOpenFolder={(id) => navigate(`/folders/${id}`)} />}
      </Suspense>
    </div>
  );
}

function NewFolderDialog({ parentId, onClose }: { parentId: string; onClose(made: boolean): void }) {
  const toast = useToast();
  const [name, setName] = useState('Untitled folder');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const n = await api<DriveNode>('/api/folders', { body: { parentId, name } });
      toast.show(`Folder “${n.name}” made.`);
      onClose(true);
    } catch (e) { setError((e as Error).message); setBusy(false); }
  };
  return (
    <Dialog open onClose={() => onClose(false)} title="New folder" actions={<>
      <button type="button" className="dz-btn dz-btn--ghost" onClick={() => onClose(false)}>Cancel</button>
      <button type="button" className="dz-btn dz-btn--primary" onClick={save} disabled={busy} data-testid="new-folder-save">Make folder</button>
    </>}>
      <form onSubmit={(e) => { e.preventDefault(); save(); }}>
        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} autoFocus onFocus={(e) => e.target.select()} error={error ?? undefined} maxLength={255} data-testid="new-folder-name" />
      </form>
    </Dialog>
  );
}
